/**
 * Service Form Component
 * Task 38: Reusable service form for creating/editing services
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { IndustryService } from '@/types/industry';
import { CompanyType } from '@/types/company';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const serviceSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  description: z.string().optional(),
  price: z.coerce.number().min(0, 'Preço deve ser maior ou igual a zero'),
  duration: z.coerce.number().min(0, 'Duração deve ser maior ou igual a zero').optional(),
  category: z.string().optional(),
  is_active: z.boolean().default(true),
});

type ServiceFormData = z.infer<typeof serviceSchema>;

interface ServiceFormProps {
  service?: IndustryService;
  domain: CompanyType;
  existingCategories?: string[];
  onSubmit: (data: ServiceFormData) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

const DOMAIN_TERMINOLOGY: Record<string, { singular: string; categoryLabel: string; categoryPlaceholder: string }> = {
  [CompanyType.HEALTH_CLINICS]: {
    singular: 'Procedimento',
    categoryLabel: 'Especialidade',
    categoryPlaceholder: 'Ex: Clínica Geral, Cardiologia',
  },
  [CompanyType.VETERINARY]: {
    singular: 'Procedimento',
    categoryLabel: 'Tipo',
    categoryPlaceholder: 'Ex: Consulta, Cirurgia, Vacina',
  },
  [CompanyType.BEAUTY_CLINICS]: {
    singular: 'Procedimento',
    categoryLabel: 'Categoria',
    categoryPlaceholder: 'Ex: Estética Facial, Corporal',
  },
  [CompanyType.BEAUTY_SALONS]: {
    singular: 'Serviço',
    categoryLabel: 'Categoria',
    categoryPlaceholder: 'Ex: Cabelo, Unha, Depilação',
  },
  [CompanyType.PILATES_YOGA]: {
    singular: 'Aula/Pacote',
    categoryLabel: 'Tipo',
    categoryPlaceholder: 'Ex: Pilates Solo, Mat Pilates, Yoga',
  },
  [CompanyType.AUTO_REPAIR]: {
    singular: 'Serviço',
    categoryLabel: 'Categoria',
    categoryPlaceholder: 'Ex: Mecânica, Elétrica, Funilaria',
  },
};

const DURATION_PRESETS = [
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '1 hora', value: 60 },
  { label: '1h 30min', value: 90 },
  { label: '2 horas', value: 120 },
  { label: '3 horas', value: 180 },
  { label: '4 horas', value: 240 },
];

export function ServiceForm({
  service,
  domain,
  existingCategories = [],
  onSubmit,
  onCancel,
  isSubmitting = false,
}: ServiceFormProps) {
  const terminology = DOMAIN_TERMINOLOGY[domain] || {
    singular: 'Serviço',
    categoryLabel: 'Categoria',
    categoryPlaceholder: 'Ex: Categoria do serviço',
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    defaultValues: service
      ? {
          name: service.name,
          description: service.description || '',
          price: service.price,
          duration: service.duration || undefined,
          category: service.category || '',
          is_active: service.is_active,
        }
      : {
          name: '',
          description: '',
          price: 0,
          duration: undefined,
          category: '',
          is_active: true,
        },
  });

  const isActive = watch('is_active');
  const selectedCategory = watch('category');

  const handleFormSubmit = async (data: ServiceFormData) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
            <CardDescription>
              Dados principais do {terminology.singular.toLowerCase()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Nome do {terminology.singular} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                {...register('name')}
                placeholder={`Nome do ${terminology.singular.toLowerCase()}`}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Descrição detalhada (opcional)"
                rows={3}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">{terminology.categoryLabel}</Label>
              {existingCategories.length > 0 ? (
                <Select
                  value={selectedCategory || 'none'}
                  onValueChange={(value) => setValue('category', value === 'none' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={terminology.categoryPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {existingCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__">+ Nova Categoria</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="category"
                  {...register('category')}
                  placeholder={terminology.categoryPlaceholder}
                />
              )}
              {errors.category && (
                <p className="text-sm text-destructive">{errors.category.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pricing & Duration */}
        <Card>
          <CardHeader>
            <CardTitle>Preço e Duração</CardTitle>
            <CardDescription>Informações comerciais do serviço</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="price">
                  Preço (R$) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('price')}
                  placeholder="0,00"
                />
                {errors.price && (
                  <p className="text-sm text-destructive">{errors.price.message}</p>
                )}
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label htmlFor="duration">Duração (minutos)</Label>
                <Select
                  value={watch('duration')?.toString() || 'none'}
                  onValueChange={(value) =>
                    setValue('duration', value === 'none' ? undefined : parseInt(value, 10))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a duração" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Não especificada</SelectItem>
                    {DURATION_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value.toString()}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.duration && (
                  <p className="text-sm text-destructive">{errors.duration.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status */}
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
            <CardDescription>Disponibilidade do serviço</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="is_active">Serviço Ativo</Label>
                <p className="text-sm text-muted-foreground">
                  {isActive
                    ? 'Serviço disponível para agendamento'
                    : 'Serviço indisponível para novos agendamentos'}
                </p>
              </div>
              <Switch
                id="is_active"
                checked={isActive}
                onCheckedChange={(checked) => setValue('is_active', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : service ? 'Atualizar' : 'Criar'} {terminology.singular}
          </Button>
        </div>
      </div>
    </form>
  );
}
